<template>
  <div>
    <b-overlay
      id="overlay-background"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <div>
        <FilterCard :applied-filter-count="appliedFilterCount">
          <b-row>
            <b-col
              cols="12"
              md="3"
            >
              <b-form-group
                label="Merchant Ref No"
                label-for="v-ref-no"
              >
                <b-form-input
                  id="v-ref-no"
                  v-model="filters['mcnt_merchants.ref_no']"
                  placeholder="Search"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="3"
            >
              <b-form-group
                label="Merchant"
                label-for="merchant"
              >
                <v-select
                  v-model="filters['merchant_id']"
                  :options="merchantOptions"
                  :reduce="option => option.id"
                  label="name"
                  placeholder="Select Merchant"
                >
                  <template v-slot:option="option">
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
          </b-row>
          <!-- Apply and Clear Filters -->
          <b-row class="mt-1">
            <b-col>
              <filter-buttons
                :filter-data="filters"
                :apply-filters="applyFilters"
                :clear-filters="clearFilters"
              />
            </b-col>
          </b-row>
        </FilterCard>
      </div>
      <div>
        <b-card
          no-body
        >
          <div
            class="m-2"
          >
            <b-row>
              <b-col
                class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
              >
                <div>
                  <ReportTableButtons
                    :items="itemsWithTotals"
                    :json_fieldz="json_fields"
                    :name="`${downloadReportName} - Page ${page}.xls`"
                    :bulkname="`${downloadReportName}.xls`"
                    :fetch="fetchToBeInvoicedNoPagination"
                    @refresh="fetchToBeInvoiced"
                  />
                </div>
              </b-col>
            </b-row>
          </div>
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :items="items"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
            @sort-changed="sortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="items"
                :data-loading="dataLoading"
              />
            </template>
            <template #head()="{ field }">
              <div
                class="sortable-header"
              >
                <span class="header-text">{{ field.label }}</span>
                <feather-icon
                  v-if="field.sortable"
                  :icon="sort === field.key
                    ? 'ArrowUpIcon'
                    : 'ArrowDownIcon'"
                  size="14"
                />
              </div>
            </template>
            <template #cell(order_count)="data">
              {{ __numberWithCommas(data.value) }}
            </template>
            <template #cell(to_be_invoice_amount)="data">
              {{ __numberWithCommas(data.value, true) }}
            </template>
            <template #cell(tomorrow_amount)="data">
              <span v-if="data.value !== null">{{ __numberWithCommas(data.value, true) }}</span>
              <span v-else>-</span>
            </template>
            <template #cell(day_after_tomorrow_amount)="data">
              <span v-if="data.value !== null">{{ __numberWithCommas(data.value, true) }}</span>
              <span v-else>-</span>
            </template>
            <template #bottom-row>
              <td
                colspan="3"
                class="text-right font-weight-bolder"
              >
                Total
              </td>
              <td class="text-right font-weight-bolder">
                {{ __numberWithCommas(totalOrderCount) }}
              </td>
              <td class="text-right font-weight-bolder">
                {{ __numberWithCommas(totalToBeInvoiceAmount, true) }}
              </td>
              <td />
              <td class="text-right font-weight-bolder">
                {{ __numberWithCommas(totalTomorrowAmount, true) }}
              </td>
              <td class="text-right font-weight-bolder">
                {{ __numberWithCommas(totalDayAfterTomorrowAmount, true) }}
              </td>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                md="3"
                class="d-flex align-items-center justify-content-sm-start"
              >
                <label class="mr-1">Per page</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="pageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block ml-1 width-100"
                />
              </b-col>
              <!-- Pagination -->
              <b-col
                cols="12"
                md="9"
                class="d-flex align-items-center justify-content-sm-end"
              >
                <span class="text-muted pagination-text  mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
                <b-pagination
                  v-model="page"
                  :total-rows="(meta.total)? meta.total : 0"
                  :per-page="perPage"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>

              </b-col>
            </b-row>
          </div>
        </b-card>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BRow,
  BTable,
  VBModal,
  BOverlay,
  BPagination,
  VBTooltip,

} from 'bootstrap-vue'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import moment from 'moment'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import Ripple from 'vue-ripple-directive'
import FilterButtons from '@/components/Filter/FilterButtons.vue'
import tableMixin from '@core/mixins/tableMixin'
import FilterCard from '@/components/FilterCard/FilterCard.vue'
import getMerchantList from './MerchantListDropdown'

const ReportRepository = RepositoryFactory.get('report')

export default {
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    FilterCard,
    vSelect,
    BCard,
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BOverlay,
    BPagination,
    BFormInput,
    FilterButtons,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, tableMixin],
  data() {
    return {
      filters: {
        merchant_id: null,
      },
      tableData: [],
      dataLoading: false,
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50, 100, 200],
      totalRows: 1,
      page: 1,
      meta: {},
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      merchantOptions: [],
      fields: [
        {
          key: 'merchant_ref_no',
          label: 'MERCHANT REF NO',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'merchant_name',
          label: 'MERCHANT',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'merchant_contact_number',
          label: 'MERCHANT CONTACT NO',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'order_count',
          label: 'ORDER COUNT',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'to_be_invoice_amount',
          label: 'TO BE INVOICED AMOUNT',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'next_invoice_date',
          label: 'NEXT INVOICE DATE',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'tomorrow_amount',
          label: 'TOMORROW',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'day_after_tomorrow_amount',
          label: 'DAY AFTER TOMORROW',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
      ],
      json_fields: {
        'MERCHANT REF NO': {
          field: 'merchant_ref_no',
          callback: value => `"${value}"`,
        },
        MERCHANT: {
          field: 'merchant_name',
          callback: value => `"${value}"`,
        },
        'MERCHANT CONTACT NO': {
          field: 'merchant_contact_number',
          callback: value => `"${value}"`,
        },
        'ORDER COUNT': {
          field: 'order_count',
          callback: value => `"${value}"`,
        },
        'TO BE INVOICE AMOUNT': {
          field: 'to_be_invoice_amount',
          callback: value => `"${value}"`,
        },
        'NEXT INVOICE DATE': {
          field: 'next_invoice_date',
          callback: value => `"${value}"`,
        },
        TOMORROW: {
          field: 'tomorrow_amount',
          callback: value => (value !== null ? `"${value}"` : '"-"'),
        },
        'DAY AFTER TOMORROW': {
          field: 'day_after_tomorrow_amount',
          callback: value => (value !== null ? `"${value}"` : '"-"'),
        },
      },
      currentDate: '',
      todayFilterQuery: '',
      downloadReportName: 'To-Be-Invoiced-Report',
      items: [],
    }
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
    totalOrderCount() {
      return this.items.reduce((total, item) => total + (Number(item.order_count) || 0), 0)
    },
    totalToBeInvoiceAmount() {
      return this.items.reduce((total, item) => total + (Number(item.to_be_invoice_amount) || 0), 0)
    },
    totalTomorrowAmount() {
      return this.items.reduce((total, item) => total + (Number(item.tomorrow_amount) || 0), 0)
    },
    totalDayAfterTomorrowAmount() {
      return this.items.reduce((total, item) => total + (Number(item.day_after_tomorrow_amount) || 0), 0)
    },
    itemsWithTotals() {
      if (this.items.length === 0) return []
      const totalsRow = {
        merchant_ref_no: 'Total',
        merchant_name: '-',
        merchant_contact_number: '-',
        order_count: this.totalOrderCount,
        to_be_invoice_amount: this.totalToBeInvoiceAmount,
        next_invoice_date: '-',
        tomorrow_amount: this.totalTomorrowAmount,
        day_after_tomorrow_amount: this.totalDayAfterTomorrowAmount,
      }
      return [...this.items, totalsRow]
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchToBeInvoiced()
      },
    },
    deep: true,
    page() {
      this.fetchToBeInvoiced()
    },
    perPage() {
      this.fetchToBeInvoiced()
    },
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.items.length
    this.fetchToBeInvoiced()
    this.activeMerchants()
    this.updateFilterCount()
  },
  methods: {
    async fetchToBeInvoiced() {
      this.dataLoading = true
      this.items = []
      try {
        // eslint-disable-next-line no-underscore-dangle
        this.filterQuery = this.__refineDateQuery(this.filterQuery, [this.filters])

        // Get data from API
        if (!this.filterQuery) {
          this.filterQuery = this.todayFilterQuery
        }
        const { data } = (await ReportRepository.getToBeInvoicedReport(this.page, this.filterQuery, this.perPage))

        // fiter the default date or selected date for table data and meta data
        this.items = this.formatTableData(data.data)
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchToBeInvoicedNoPagination() {
      try {
        const { data } = (await ReportRepository.getToBeInvoicedReportNoPagiantion(this.filterQuery))
        const formattedData = this.formatTableData(data.data)

        if (formattedData.length > 0) {
          const totalOrderCount = formattedData.reduce((sum, item) => sum + (Number(item.order_count) || 0), 0)
          const totalToBeInvoiceAmount = formattedData.reduce((sum, item) => sum + (Number(item.to_be_invoice_amount) || 0), 0)
          const totalTomorrowAmount = formattedData.reduce((sum, item) => sum + (Number(item.tomorrow_amount) || 0), 0)
          const totalDayAfterTomorrowAmount = formattedData.reduce((sum, item) => sum + (Number(item.day_after_tomorrow_amount) || 0), 0)

          formattedData.push({
            merchant_ref_no: 'Total',
            merchant_name: '-',
            merchant_contact_number: '-',
            order_count: totalOrderCount,
            to_be_invoice_amount: totalToBeInvoiceAmount,
            next_invoice_date: '-',
            tomorrow_amount: totalTomorrowAmount,
            day_after_tomorrow_amount: totalDayAfterTomorrowAmount,
          })
        }

        return formattedData
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    async activeMerchants() {
      this.merchantOptions = await getMerchantList()
    },
    handleChangePage(page) {
      this.page = page
      this.fetchToBeInvoiced()
    },
    applyFilters() {
      this.fetchToBeInvoiced()
      this.updateFilterCount()
    },
    clearFilters() {
      this.filters = {}
      this.$nextTick(() => {
        this.fetchToBeInvoiced()
      })
      this.updateFilterCount()
    },
    clearDate(filterKey) {
      this.filters[filterKey] = ''
    },
    formatTableData(items) {
      return items.map(item => {
        const nextInvoiceDateStr = item.merchant_data && item.merchant_data.merchant_setting && item.merchant_data.merchant_setting.next_invoice_generate_date
          ? item.merchant_data.merchant_setting.next_invoice_generate_date.split(' ')[0]
          : null

        let isTomorrow = false
        let isDayAfterTomorrow = false
        if (nextInvoiceDateStr) {
          const date = moment(nextInvoiceDateStr, 'YYYY-MM-DD').startOf('day')
          const tomorrow = moment().add(1, 'days').startOf('day')
          const dayAfter = moment().add(2, 'days').startOf('day')
          isTomorrow = date.isSame(tomorrow)
          isDayAfterTomorrow = date.isSame(dayAfter)
        }

        return {
          ...item,
          next_invoice_date: nextInvoiceDateStr || '-',
          tomorrow_amount: isTomorrow ? item.to_be_invoice_amount : null,
          day_after_tomorrow_amount: isDayAfterTomorrow ? item.to_be_invoice_amount : null,
        }
      })
    },
  },
}
</script>
<style lang="scss">
@import '../../../@core/scss/vue/libs/vue-select';
@import '../../../@core/scss/vue/libs/vue-flatpicker';
</style>
